import type { QueryClient, UseMutationOptions, UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { upsertTripChatRecentSearch } from 'tripPlanner/api'
import type { TripChatRecentSearch } from 'tripPlanner/api/types'
import type { UpsertTripChatRecentSearchBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'
import * as ChatKeys from '../reactQueryKeys/chat'

export type UpsertTripChatRecentSearchParams = {
  chatId: number
  body: UpsertTripChatRecentSearchBody
}

export type UpsertTripChatRecentSearchResult = TripChatRecentSearch

// All mounts sharing this query cache serialize writes to the same saved-search slot.
const pendingSearchWrites = new WeakMap<QueryClient, Map<string, Promise<void>>>()

async function persistInOrder(queryClient: QueryClient, { chatId, body }: UpsertTripChatRecentSearchParams) {
  let pending = pendingSearchWrites.get(queryClient)
  if (!pending) {
    pending = new Map()
    pendingSearchWrites.set(queryClient, pending)
  }
  const key = `${chatId}:${body.surface}:${body.tripStopId ?? 'unscoped'}`
  const previous = pending.get(key) ?? Promise.resolve()
  const request = previous.then(() => upsertTripChatRecentSearch(chatId, body))
  const settled = request.then(() => undefined, () => undefined)
  pending.set(key, settled)
  try {
    return await request
  } finally {
    if (pending.get(key) === settled) pending.delete(key)
  }
}

export const useUpsertTripChatRecentSearchMutation = (
  options?: UseMutationOptions<
    UpsertTripChatRecentSearchResult,
    unknown,
    UpsertTripChatRecentSearchParams
  >,
): UseMutationResult<
  UpsertTripChatRecentSearchResult,
  unknown,
  UpsertTripChatRecentSearchParams
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: params => persistInOrder(queryClient, params),
    ...options,
    onSuccess: async(data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: ChatKeys.itineraryDetail(variables.chatId),
      })
      await options?.onSuccess?.(data, variables, context)
    },
  })
}
